import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";

const STAGES = [
  { name: "Sense", body: "Receptors in your feet, joints, muscles and fascia constantly report pressure, stretch, position and load — millions of signals every second." },
  { name: "Process", body: "Your spinal cord and brain combine that input with past experience, stress and expectation to build a live map of your body and decide what it needs." },
  { name: "Move", body: "The brain sends commands back out — setting muscle tone, posture, balance and the movement strategy you use for every step, lift and breath." },
  { name: "Feedback", body: "Every movement creates new sensory information. The nervous system compares what happened with what it planned, and notices what feels safe or threatened." },
  { name: "Adapt", body: "Over time the system rewires around repeated input — for better or worse. Good input builds confident, efficient movement; restricted input builds guarding and pain." },
];
const R = 150;
const pos = (i) => {
  const a = (i / STAGES.length) * 2 * Math.PI - Math.PI / 2;
  return { x: 200 + R * Math.cos(a), y: 200 + R * Math.sin(a) };
};

function useLoop(count) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!inView || paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 3600);
    return () => clearInterval(t);
  }, [inView, paused, count]);
  return { ref, idx, pick: (i) => { setPaused(true); setIdx(i); } };
}

function LoopDial({ idx, pick }) {
  const p = pos(idx + 0.5);
  return (
    <svg viewBox="0 0 400 400" className="w-full max-w-[440px] mx-auto" data-testid="nervous-loop-dial">
      <circle cx={200} cy={200} r={R} fill="none" stroke="#FAF7F2" strokeOpacity={0.15} strokeWidth={1} />
      <motion.circle cx={200} cy={200} r={R} fill="none" stroke="#C5A880" strokeWidth={1.5} strokeDasharray="3 9"
        animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }} style={{ originX: "200px", originY: "200px" }} />
      <motion.circle r={6} fill="#C5A880" animate={{ cx: p.x, cy: p.y }} transition={{ duration: 0.9, ease: "easeInOut" }} />
      <text x={200} y={194} textAnchor="middle" className="fill-ivory/50 text-[11px] uppercase tracking-[0.3em]">Nervous</text>
      <text x={200} y={212} textAnchor="middle" className="fill-ivory/50 text-[11px] uppercase tracking-[0.3em]">System</text>
      {STAGES.map((s, i) => {
        const { x, y } = pos(i);
        const on = i === idx;
        return (
          <g key={s.name} role="button" tabIndex={0} aria-label={s.name} className="cursor-pointer focus:outline-none" data-testid={`nervous-loop-stage-${i}`}
            onClick={() => pick(i)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(i); } }}>
            <circle cx={x} cy={y} r={34} className="transition-all duration-500" fill={on ? "#C5A880" : "#2A2421"} stroke="#C5A880" strokeOpacity={on ? 1 : 0.5} />
            <text x={x} y={y + 4} textAnchor="middle" className={`font-serif text-[13px] transition-all duration-500 ${on ? "fill-espresso" : "fill-ivory"}`}>{s.name}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Animated SENSE → PROCESS → MOVE → FEEDBACK → ADAPT loop.
export function NervousLoop() {
  const { ref, idx, pick } = useLoop(STAGES.length);
  const s = STAGES[idx];
  return (
    <section ref={ref} className="bg-espresso text-ivory py-24 lg:py-36 px-6 md:px-10 lg:px-14" data-testid="nervous-loop">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-x-14 gap-y-10 content-center">
        <Reveal className="lg:col-span-6 lg:col-start-7 lg:row-start-1 self-end">
          <div className="overline !text-brass mb-6">The loop that runs you</div>
          <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] mb-4">Sense. Process. Move. Feedback. <span className="italic text-brass">Adapt.</span></h2>
          <p className="text-ivory/65 font-light text-lg max-w-xl">Your nervous system runs this loop continuously. Our care improves the input, so every lap of the loop gets a little better.</p>
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-1 lg:row-start-1 lg:row-span-2 self-center"><LoopDial idx={idx} pick={pick} /></Reveal>
        <div className="lg:col-span-6 lg:col-start-7 lg:row-start-2">
          <div className="min-h-[170px] border-t border-ivory/10 pt-8">
            <AnimatePresence mode="wait">
              <motion.div key={s.name} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }} data-testid="nervous-loop-detail">
                <div className="text-xs text-brass tracking-[0.2em] mb-2">{String(idx + 1).padStart(2, "0")} / 05</div>
                <h3 className="font-serif text-3xl lg:text-4xl mb-3" data-testid="nervous-loop-stage-name">{s.name}</h3>
                <p className="text-ivory/75 font-light leading-relaxed max-w-xl">{s.body}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
